<template>
  <card-canvas :hide-back-button="mailboxCreated">
    <div class="mailbox-new">
      <div class="mailbox-new__title">
        <h4 v-if="!mailboxCreated">Create a new mailbox</h4>
        <h4 v-else class="scripty">Invite user to this mailbox&hellip;</h4>
        <avatar-gallery 
          v-if="mailboxCreated"
          :people="assignedPeople" 
          :max-num-avatars="6" 
          :size="28" 
          stack-tightly
        />
      </div>
      <loading-placeholder v-if="isLoading" :lines="10" class="loading" />
      <template v-else>
        <template v-if="!mailboxCreated">
          <div class="step">
            <h4>1. Name it</h4>
            <el-input ref="nameRef" v-model="name" :is-focused="focusName" maxlength="100" placeholder="e.g. Customer support, Sales inquiries, Hiring inbox" />
          </div>
          <div class="step">
            <h4>2. Select a workflow or <a href="" class="just-a-link" @click.prevent="showCreateWorkflowDrawer=true">make a new one&hellip;</a></h4>
            <entity-workflow-selector v-model="selectedWorkflowID" :workflows="workflows" :entity-type="EEntities.MAILBOXES" />
          </div>
          <div class="step">
            <h4>3. Update status on customer reply</h4>
            <div class="extra-config">
              <el-switch v-model="allowInactiveUpdateStatusChange" />
              <div :class="{'disabled' : !allowInactiveUpdateStatusChange}">
                <p>When a customer replies to an <strong>inactive conversation</strong>, update
                  the status to&hellip;</p>
                <status-picker v-model:selected-id="inactiveUpdateStatusID" :statuses="statusesForSelection" />
              </div>
            </div>
          </div>
          <div v-if="$permissions.jobsFeatureEnabled && selectedWorkflowHasJobs" class="step">
            <h4>4. Workflow Jobs</h4>
            <job-toggler
              v-model:jobs-enabled="enableJobs"
              :status-job-mappings="selectedWorkflow.listStatusJobMappings"
              :statuses="selectedWorkflow.listStatuses"
              :jobs="selectedWorkflow.listJobs"
              :entity-type="EEntities.MAILBOXES"
            />
          </div>
          <dd-button 
            :is-loading="isSubmitting"
            :color="ColorAssistant.MailboxColor"
            size="large"
            new-icon
            @click="create"
          >
            Create Mailbox
          </dd-button>
        </template>
        <template v-else>
          <div class="step">
            <entity-membership-manager
              :entity-type="EEntities.MAILBOXES"
              :entity-id="newMailboxID" 
              hide-success-messages
              @memberships-updated="(val) => assignedPeople = val"
            />
          </div>
          <dd-button 
            :color="ColorAssistant.MailboxColor"
            size="large"
            @click="goToMailboxSettingsDirectConnection()"
          >
            Connect your mailbox...
          </dd-button>
        </template>
      </template>
    </div>
    <create-workflow-drawer 
      v-model:show-drawer="showCreateWorkflowDrawer"
      :entity-type="EEntities.MAILBOXES"
      @workflow-created="(id) => handleWorkflowCreation(id)"
    />
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import CardCanvas from '@/layouts/card-canvas.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import {
  IPersonBase,
  IStatusForSelection,
  IWorkflowForSelection,
} from '@/api/types/response.types'
import { IMailboxesInputCreate } from '@/api/types/request.types'
import { EEntities } from '@/types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import Avatar from '@/components/global/avatar.vue'

export default defineComponent({

  components: {
    CardCanvas,
    Avatar,
  },

  data() {
    return {
      EEntities,
      ColorAssistant,
      RouteAssistant,
      focusName: true,
      isLoading: true,
      isSubmitting: false,
      workflows: [] as IWorkflowForSelection[],
      showCreateWorkflowDrawer: false,
      newMailboxID: 0,
      assignedPeople: [] as IPersonBase[],
      allowInactiveUpdateStatusChange: true,

      // Input values
      name: '',
      selectedWorkflowID: null as any,
      inactiveUpdateStatusID: 0,
      enableJobs: this.$permissions.jobsFeatureEnabled,
    }
  },
  head: {
    title: 'New Mailbox',
  },

  computed: {
    mailboxCreated(): boolean {
      return this.newMailboxID !== 0
    },
    selectedWorkflow(): IWorkflowForSelection {
      return this.workflows.find(w => w.id === this.selectedWorkflowID)
    },
    selectedWorkflowHasJobs(): boolean {
      if (!this.selectedWorkflow) {
        return false
      }
      return this.selectedWorkflow.listJobs.length > 0
    },
    statusesForSelection(): IStatusForSelection[] {
      return this.selectedWorkflow.listStatuses
    },
  },

  watch: {
    selectedWorkflowID: {
      handler(val) {
        // Default inactive update status id to the first...
        this.inactiveUpdateStatusID = this.selectedWorkflow.listStatuses[0].id
      },
    },
  },

  async created() {
    this.isLoading = true
    await this.getWorkflows()
    // Selected before the form renders, which reads the selected workflow's statuses and jobs
    this.selectedWorkflowID = this.workflows[0].id

    if (
      this.$route.query.workflow_id &&
      this.workflows.some(
        w => w.id.toString() === this.$route.query.workflow_id.toString(),
      )
    ) {
      // Query values are strings; workflow IDs are numbers
      this.selectedWorkflowID = Number(this.$route.query.workflow_id)
    }
    this.isLoading = false

    this.$nextTick(() => {
      // @ts-ignore
      this.$refs.nameRef.focus()
    })
  },

  methods: {
    goToMailboxSettingsDirectConnection() {
      LocalStore.setShowInfoOnEntityLanding(this.newMailboxID)
      RouteAssistant.goToMailboxSettingsDirectConnectionRoute(
        this.$router,
        this.$route.params.accountId,
        this.newMailboxID,
      )
    },
    async handleWorkflowCreation(id: number) {
      await this.getWorkflows()

      const selectedWorkflow = this.workflows.find(w => w.id === id)
      const selectedIndex = this.workflows.findIndex(w => w.id === id)

      this.workflows.splice(selectedIndex, 1)
      this.workflows.unshift(selectedWorkflow)

      this.selectedWorkflowID = id
    },
    async getWorkflows() {
      this.workflows = await MailboxesAPI.get_Workflows()
    },
    async create() {
      if (this.name.trim().length === 0) {
        this.focusName = false
        await this.$nextTick()
        this.focusName = true
        this.$messaging.showError('Please enter a name.')
        return
      }
      this.isSubmitting = true
      try {
        const data = {
          name: this.name,
          workflowID: this.selectedWorkflowID,
          enableJobs: this.enableJobs,
          inactiveUpdateStatusID: this.allowInactiveUpdateStatusChange
            ? this.inactiveUpdateStatusID
            : null,
        } as IMailboxesInputCreate
        const response = await MailboxesAPI.post_Create(data)
        this.newMailboxID = response.id
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-new {
  .scripty {
    font-size: 26px !important;
  }

  &__title {
    background-color: var(--color-background-header);
    top: $navbar-desktop-height;
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(8px);
    margin-bottom: 20px;
    border-bottom: $border--dotted-medium;

    .avatar-gallery {
      margin-left: auto;
    }

    @include media-breakpoint-down(sm) {
      top: $navbar-mobile-height;
    }

    > .ddi {
      color: var(--color-text-third);
      top: -4px;
    }
  }

  .step {
    margin-bottom: 40px;

    .extra-config {
      display: flex;
      align-items: center;
      border: $border--dotted-light;
      border-radius: 50px;
      background-color: var(--color-background-primary);
      padding: 10px;
      margin-bottom: 20px;
      @include flex-row-horizontal-spacing(8px);

      > div {
        display: flex;
        align-items: center;
        @include flex-row-horizontal-spacing(8px);

        &.disabled {
          opacity: 0.5;
          pointer-events: none;
        }
      }
    }
  }
  .dd-button {
    margin-left: auto;
  }
}
</style>
